import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '../../store/authStore';
import { usersApi, companiesApi, devicesApi } from '../../services/api';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Modal } from '../../components/common/Modal';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'EMPLOYEE' | 'CLIENT' | 'REPO_AGENT';
  isActive: boolean;
  companyId: string | null;
  company?: {
    id: string;
    name: string;
  };
  assignedCompanies?: { companyId: string; company: { id: string; name: string } }[];
  assignedDevices?: { deviceId: string; device: { id: string; imei: string; name: string | null } }[];
}

interface Company {
  id: string;
  name: string;
  code: string;
}

interface Device {
  id: string;
  imei: string;
  name: string | null;
}

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'EMPLOYEE' | 'CLIENT' | 'REPO_AGENT';
  companyId?: string;
  isActive: boolean;
}

const ROLES = [
  { value: 'SUPERADMIN', label: 'Super Admin', color: 'bg-red-100 text-red-800' },
  { value: 'ADMIN', label: 'Administrador', color: 'bg-purple-100 text-purple-800' },
  { value: 'EMPLOYEE', label: 'Empleado', color: 'bg-blue-100 text-blue-800' },
  { value: 'CLIENT', label: 'Cliente', color: 'bg-green-100 text-green-800' },
  { value: 'REPO_AGENT', label: 'Incautador', color: 'bg-orange-100 text-orange-800' },
];

export function UsersPage() {
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isAdmin = isSuperAdmin || user?.role === 'ADMIN';
  // Only the platform operator may assign the SUPERADMIN role; a tenant ADMIN may
  // create the other roles (always confined to its own company by the backend).
  const assignableRoles = isSuperAdmin ? ROLES : ROLES.filter((r) => r.value !== 'SUPERADMIN');
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignCompaniesModal, setShowAssignCompaniesModal] = useState(false);
  const [showAssignDevicesModal, setShowAssignDevicesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
    companyId: '',
    isActive: true,
  });

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersApi.list({ limit: 100 });
      return response.data.data as User[];
    },
    enabled: isAdmin,
  });

  // Fetch companies for assignment
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesApi.list({ limit: 100 });
      // API returns { companies: [], total, page, limit } via sendPaginated
      const data = response.data.data;
      return (Array.isArray(data) ? data : data?.companies || []) as Company[];
    },
    enabled: isAdmin,
  });

  // Fetch devices for REPO_AGENT assignment
  const { data: devicesData } = useQuery({
    queryKey: ['devices', { limit: 100 }],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 100 });
      // All queries under the ['devices', ...] key MUST return Device[] —
      // pages sharing the key receive each other's cached data as-is.
      const data = response.data.data;
      return (Array.isArray(data) ? data : data?.devices || []) as Device[];
    },
    enabled: isAdmin,
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (data: UserFormData) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      resetForm();
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UserFormData> }) =>
      usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowEditModal(false);
      setSelectedUser(null);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDeleteModal(false);
      setSelectedUser(null);
    },
  });

  // Assign companies mutation
  const assignCompaniesMutation = useMutation({
    mutationFn: ({ id, companyIds }: { id: string; companyIds: string[] }) =>
      usersApi.assignCompanies(id, companyIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowAssignCompaniesModal(false);
      setSelectedUser(null);
      setSelectedCompanyIds([]);
    },
  });

  // Assign devices mutation
  const assignDevicesMutation = useMutation({
    mutationFn: ({ id, deviceIds }: { id: string; deviceIds: string[] }) =>
      usersApi.assignDevices(id, deviceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowAssignDevicesModal(false);
      setSelectedUser(null);
      setSelectedDeviceIds([]);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'EMPLOYEE',
      companyId: '',
      isActive: true,
    });
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEdit = (u: User) => {
    setSelectedUser(u);
    setFormData({
      name: u.name,
      email: u.email,
      password: '',
      role: u.role,
      companyId: u.companyId || '',
      isActive: u.isActive,
    });
    setShowEditModal(true);
  };

  const handleDelete = (u: User) => {
    setSelectedUser(u);
    setShowDeleteModal(true);
  };

  const handleAssignCompanies = (u: User) => {
    setSelectedUser(u);
    setSelectedCompanyIds(u.assignedCompanies?.map((ac) => ac.companyId) || []);
    setShowAssignCompaniesModal(true);
  };

  const handleAssignDevices = (u: User) => {
    setSelectedUser(u);
    setSelectedDeviceIds(u.assignedDevices?.map((ad) => ad.deviceId) || []);
    setShowAssignDevicesModal(true);
  };

  const users = Array.isArray(usersData) ? usersData : [];
  const companies = Array.isArray(companiesData) ? companiesData : [];
  const devices = Array.isArray(devicesData) ? devicesData : [];

  // Filter users
  const filteredUsers = users.filter((u) => {
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      if (
        !u.name.toLowerCase().includes(search) &&
        !u.email.toLowerCase().includes(search)
      ) {
        return false;
      }
    }
    if (roleFilter && u.role !== roleFilter) {
      return false;
    }
    return true;
  });

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            No tienes permisos para ver esta pagina.
          </p>
        </div>
      </div>
    );
  }

  const getRoleInfo = (role: string) => {
    return ROLES.find((r) => r.value === role) || ROLES[1];
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-gray-600">Gestióna los usuarios del sistema</p>
        </div>
        <Button onClick={handleCreate}>Crear usuario</Button>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div className="w-64">
            <Input
              placeholder="Buscar por nombre o email..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los roles</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {(searchFilter || roleFilter) && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchFilter('');
                setRoleFilter('');
              }}
            >
              Limpiar filtros
            </Button>
          )}
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Mostrando {filteredUsers.length} de {users.length} usuarios
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((u) => {
                const roleInfo = getRoleInfo(u.role);
                return (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{u.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded ${roleInfo.color}`}>
                        {roleInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {u.company?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          u.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {u.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(u)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Editar
                        </button>
                        {u.role === 'EMPLOYEE' && (
                          <button
                            onClick={() => handleAssignCompanies(u)}
                            className="text-sm text-green-600 hover:text-green-800"
                          >
                            Empresas
                          </button>
                        )}
                        {u.role === 'REPO_AGENT' && (
                          <button
                            onClick={() => handleAssignDevices(u)}
                            className="text-sm text-orange-600 hover:text-orange-800"
                          >
                            Dispositivos
                          </button>
                        )}
                        {u.id !== user?.id && (
                          <button
                            onClick={() => handleDelete(u)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                    No se encontraron usuarios
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Crear usuario"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createUserMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contrasena *
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol *
            </label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value as UserFormData['role'] })
              }
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {assignableRoles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          {(formData.role === 'CLIENT' || formData.role === 'REPO_AGENT') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Seleccionar empresa...</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              Usuario activo
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={createUserMutation.isPending}>
              {createUserMutation.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar usuario"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (selectedUser) {
              const updateData: Partial<UserFormData> = {
                name: formData.name,
                email: formData.email,
                role: formData.role,
                isActive: formData.isActive,
              };
              if (formData.password) {
                updateData.password = formData.password;
              }
              if (formData.companyId) {
                updateData.companyId = formData.companyId;
              }
              updateUserMutation.mutate({ id: selectedUser.id, data: updateData });
            }
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nueva contrasena (dejar vacio para mantener)
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol *
            </label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value as UserFormData['role'] })
              }
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {assignableRoles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActiveEdit"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="isActiveEdit" className="text-sm text-gray-700">
              Usuario activo
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowEditModal(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={updateUserMutation.isPending}>
              {updateUserMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Eliminar usuario"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            ¿Estas seguro de que deseas eliminar al usuario{' '}
            <strong>{selectedUser?.name}</strong>?
          </p>
          <p className="text-sm text-gray-500">Esta accion no se puede deshacer.</p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedUser && deleteUserMutation.mutate(selectedUser.id)}
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign Companies Modal */}
      <Modal
        isOpen={showAssignCompaniesModal}
        onClose={() => setShowAssignCompaniesModal(false)}
        title={`Asignar empresas a ${selectedUser?.name}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Selecciona las empresas que este empleado podra ver y gestionar.
          </p>
          <div className="max-h-60 overflow-y-auto border rounded-md p-2 space-y-2">
            {companies.map((c) => (
              <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={selectedCompanyIds.includes(c.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCompanyIds([...selectedCompanyIds, c.id]);
                    } else {
                      setSelectedCompanyIds(selectedCompanyIds.filter((id) => id !== c.id));
                    }
                  }}
                  className="rounded"
                />
                <span className="text-sm">{c.name}</span>
                <span className="text-xs text-gray-400">({c.code})</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowAssignCompaniesModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() =>
                selectedUser &&
                assignCompaniesMutation.mutate({
                  id: selectedUser.id,
                  companyIds: selectedCompanyIds,
                })
              }
              disabled={assignCompaniesMutation.isPending}
            >
              {assignCompaniesMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign Devices Modal */}
      <Modal
        isOpen={showAssignDevicesModal}
        onClose={() => setShowAssignDevicesModal(false)}
        title={`Asignar dispositivos a ${selectedUser?.name}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Selecciona los dispositivos que este incautador podra ver y gestionar.
          </p>
          <div className="max-h-60 overflow-y-auto border rounded-md p-2 space-y-2">
            {devices.map((d) => (
              <label key={d.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={selectedDeviceIds.includes(d.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedDeviceIds([...selectedDeviceIds, d.id]);
                    } else {
                      setSelectedDeviceIds(selectedDeviceIds.filter((id) => id !== d.id));
                    }
                  }}
                  className="rounded"
                />
                <span className="text-sm">{d.name || d.imei}</span>
                <span className="text-xs text-gray-400">({d.imei})</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowAssignDevicesModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() =>
                selectedUser &&
                assignDevicesMutation.mutate({
                  id: selectedUser.id,
                  deviceIds: selectedDeviceIds,
                })
              }
              disabled={assignDevicesMutation.isPending}
            >
              {assignDevicesMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
